import React, { useState, useEffect, useRef } from 'react';
import { Home, User, Bag, Message, Wallet, Folder } from 'react-iconly';
import { useLocation } from 'react-router-dom';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import profileImg from '../assets/profile.webp';
import ProfileTooltip from '../components/ProfileTooltip';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import { useTransition } from '../context/TransitionContext';
import { devError } from '../utils/devLog';
import LanguageToggle from '../components/LanguageToggle';
import { fetchProjectBySlug } from '../services/supabaseService';

// Proximity-based nav icon
const NavIcon = ({ item, isActive, isDarkTheme, onNavigate, mouseY }) => {
  const ref = useRef(null);

  // Distance from cursor to icon center (vertical axis)
  const distance = useTransform(mouseY, (val) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { y: 0, height: 0 };
    return val - bounds.y - bounds.height / 2;
  });

  // Distance → size mapping: closer = bigger
  const sizeTransform = useTransform(distance, [-120, 0, 120], [48, 68, 48]);
  const iconSizeTransform = useTransform(distance, [-120, 0, 120], [20, 28, 20]);

  // Spring physics for smooth interpolation
  const size = useSpring(sizeTransform, { mass: 0.1, stiffness: 150, damping: 12 });
  const iconSize = useSpring(iconSizeTransform, { mass: 0.1, stiffness: 150, damping: 12 });

  return (
    <div ref={ref} className="v2-tooltip-container">
      <motion.button
        className={`v2-nav-item ${isActive ? 'active' : ''}`}
        onClick={() => onNavigate(item.path)}
        aria-label={`Navigate to ${item.id}`}
        style={{
          width: size,
          height: size,
          '--nav-icon-color': isDarkTheme ? '#b0b0b0' : '#8e827c',
          '--nav-icon-active': isDarkTheme ? '#ffffff' : '#171412',
          '--nav-icon-hover': isDarkTheme ? '#ffffff' : '#171412',
          backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(241, 239, 229, 0.6)',
        }}
        whileHover={{ scale: 1.1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 10, mass: 1 }}
      >
        <motion.div style={{ fontSize: iconSize, display: 'flex' }}>
          {item.icon}
        </motion.div>
      </motion.button>
      <div className="v2-tooltip">
        {item.label}
      </div>
    </div>
  );
};

const Sidebar = ({ isDarkTheme }) => {
  const location = useLocation();
  const { startTransition } = useTransition();
  const { language, toggleLanguage, t } = useLanguage();
  const [statusData, setStatusData] = useState({ status: 'open to work' });
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const mouseY = useMotionValue(Infinity);

  const isProjectView = location.pathname.startsWith('/work/') && location.pathname !== '/work';
  const slugMatch = location.pathname.match(/\/work\/([^\/]+)/);
  const slug = slugMatch ? slugMatch[1] : null;

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('statuses')
          .select('status')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (data && !error) {
          setStatusData(data);
        }
      } catch (e) {
        devError('Failed to fetch status', e);
      }
    };
    fetchStatus();
  }, []);

  useEffect(() => {
    if (isProjectView && slug) {
      const getProject = async () => {
        try {
          const data = await fetchProjectBySlug(slug);
          if (data) setCurrentProject(data);
        } catch (e) {}
      };
      getProject();
    } else {
      setCurrentProject(null);
    }
  }, [isProjectView, slug]);

  const getStatusIcon = (statusStr) => {
    const s = statusStr?.toLowerCase() || '';
    if (s.includes('occupied')) return '🔥';
    if (s.includes('offline')) return '🙌';
    return '💪🏻';
  };

  const defaultNavItems = [
    { id: 'hero', path: '/', icon: <Home size={24} set="bold" />, label: t('nav.home') },
    { id: 'about', path: '/about', icon: <User size={24} set="bold" />, label: t('nav.about') },
    { id: 'work', path: '/work', icon: <Bag size={24} set="bold" />, label: t('nav.work') },
    { id: 'contact', path: '/contact', icon: <Message size={24} set="bold" />, label: t('nav.contact') }
  ];

  let navItems = defaultNavItems;
  if (isProjectView && currentProject) {
    navItems = [];
    if (currentProject.details_link) {
      navItems.push({
        id: 'preview',
        path: currentProject.details_link,
        isExternal: true,
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></svg>,
        label: 'Preview'
      });
    }
    if (currentProject.source_code_link) {
      navItems.push({
        id: 'github',
        path: currentProject.source_code_link,
        isExternal: true,
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" /></svg>,
        label: 'GitHub'
      });
    }
    navItems.push({
      id: 'back',
      path: '/work',
      isExternal: false,
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
      label: 'Kembali'
    });
  }

  return (
    <aside className="v2-sidebar" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingTop: '32px', paddingBottom: '32px' }}>
      {/* Top Profile Logo with IG Story Ring */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        zIndex: 10,
        visibility: location.pathname === '/' ? 'hidden' : 'visible',
        pointerEvents: location.pathname === '/' ? 'none' : 'auto'
      }}>
        <ProfileTooltip isDarkTheme={isDarkTheme}>
          <div style={{ position: 'relative' }}>
            {/* Insta Story Ring */}
            <div 
              onClick={() => setShowTooltip(!showTooltip)}
              style={{
                position: 'relative',
                width: '68px', height: '68px',
                cursor: 'pointer'
              }}
            >
              {/* Gradient Ring (with truly transparent center) */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                WebkitMaskImage: 'radial-gradient(circle, transparent 30px, black 31px)',
                maskImage: 'radial-gradient(circle, transparent 30px, black 31px)'
              }} />
              
              {/* Inner Image */}
              <div style={{
                position: 'absolute',
                top: '5px', left: '5px', right: '5px', bottom: '5px',
                borderRadius: '50%',
                overflow: 'hidden'
              }}>
                <img src={profileImg} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            </div>

            {/* Badge */}
            <div style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              backgroundColor: 'var(--v2-color-white)',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '2px solid var(--v2-color-offwhite)',
              pointerEvents: 'none'
            }}>
              {getStatusIcon(statusData.status)}
            </div>

            {/* Status Tooltip */}
            {showTooltip && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginTop: '12px',
                backgroundColor: 'var(--v2-color-charcoal)',
                color: 'var(--v2-color-white)',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '11px',
                fontFamily: 'var(--v2-font-mono)',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                zIndex: 300,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                textTransform: 'uppercase'
              }}>
                {statusData.status}
                {/* Tooltip triangle */}
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderBottom: '4px solid var(--v2-color-charcoal)'
                }} />
              </div>
            )}
          </div>
        </ProfileTooltip>
      </div>

      {/* Middle Nav Items */}
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}
        onMouseMove={(e) => mouseY.set(e.clientY)}
        onMouseLeave={() => mouseY.set(Infinity)}
      >
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavIcon
              key={item.id}
              item={item}
              isActive={isActive}
              isDarkTheme={isDarkTheme}
              onNavigate={(path) => {
                if (item.isExternal) {
                  const absolutePath = path.match(/^https?:\/\//i) ? path : `https://${path}`;
                  window.open(absolutePath, '_blank');
                } else {
                  startTransition(path);
                }
              }}
              mouseY={mouseY}
            />
          );
        })}
      </div>

      {/* Bottom Text */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 900, fontSize: '18px', lineHeight: 1.1, fontFamily: 'var(--v2-font-display)', marginBottom: '8px', color: isDarkTheme ? 'white' : 'var(--v2-color-charcoal)', transition: 'color 0.3s ease' }}>
          qalvin<br/>dev
        </div>
        <div style={{ marginTop: '12px' }}>
          <LanguageToggle />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
